import { type ActorRefFrom, type MachineImplementationsFrom, setup } from 'xstate';

import { type Event } from './permissions.machine.types';
import { Actions, Actors, Guards } from './setup';

import type {
  RequestLocationPermissionOutput,
  RequestPushNotificationPermissionOutput,
} from './permissions.machine.types';

// ─── Model ───────────────────────────────────────────────────────────────────

const model = setup({
  types: {
    events: {} as Event,
  },
  actors: {
    getPushNotificationPermissionStatus: Actors.getPushNotificationPermissionStatus,
    requestPushNotificationPermission: Actors.requestPushNotificationPermission,
    getLocationPermissionStatus: Actors.getLocationPermissionStatus,
    requestLocationPermission: Actors.requestLocationPermission,
    getBackgroundLocationPermissionStatus: Actors.getBackgroundLocationPermissionStatus,
    requestBackgroundLocationPermission: Actors.requestBackgroundLocationPermission,
  },
  actions: {
    //
    // ─── Internal Actions ────────────────────────────────────────────────

    persistPushNotificationSuppressedUntil: Actions.persistPushNotificationSuppressedUntil,
    persistSuppressedUntil: Actions.persistSuppressedUntil,
    persistLocationGrantType: Actions.persistLocationGrantType,
    clearLocationGrantType: Actions.clearLocationGrantType,
    persistBackgroundLocationPrimerSuppressedUntil:
      Actions.persistBackgroundLocationPrimerSuppressedUntil, // prettier-ignore
    persistBackgroundLocationPrimerSuppressedForever:
      Actions.persistBackgroundLocationPrimerSuppressedForever, // prettier-ignore

    // ─── External Actions ────────────────────────────────────────────────

    onPushNotificationPrimerShow: Actions.onPushNotificationPrimerShow,
    onRequestPushNotificationPermission: Actions.onRequestPushNotificationPermission,
    onSkipPushNotificationPermission: Actions.onSkipPushNotificationPermission,
    onLocationPrimerShow: Actions.onLocationPrimerShow,
    onRequestLocationPermission: Actions.onRequestLocationPermission,
    onSkipLocationPermission: Actions.onSkipLocationPermission,
    onLocationPermissionGranted: Actions.onLocationPermissionGranted,
    onLocationPermissionDenied: Actions.onLocationPermissionDenied,
    onAllowOnceDetected: Actions.onAllowOnceDetected,
    onPushNotificationPermissionGranted: Actions.onPushNotificationPermissionGranted,
    onPushNotificationPermissionDenied: Actions.onPushNotificationPermissionDenied,
    onBackgroundLocationPrimerShow: Actions.onBackgroundLocationPrimerShow,
    onSkipBackgroundLocationPermission: Actions.onSkipBackgroundLocationPermission,
    onBackgroundLocationUpgradeDialogShow: Actions.onBackgroundLocationUpgradeDialogShow,
    onBackgroundLocationPermissionGranted: Actions.onBackgroundLocationPermissionGranted,
    onBackgroundLocationPermissionDenied: Actions.onBackgroundLocationPermissionDenied,
  },
  guards: {
    //
    // ─── External Guards ─────────────────────────────────────────────────

    checkIfPushNotificationPrimerEnabled: () => false,
    checkIfLocationPrimerEnabled: () => false,
    checkIfBackgroundLocationPrimerEnabled: () => false,

    // ─── Internal Guards ─────────────────────────────────────────────────

    checkIfPushNotificationSuppressedForever: Guards.checkIfPushNotificationSuppressedForever,
    checkIfPushNotificationSuppressionActive: Guards.checkIfPushNotificationSuppressionActive,
    checkIfPushNotificationPermissionGranted: Guards.checkIfPushNotificationPermissionGranted,
    checkIfPushNotificationPermissionDenied: Guards.checkIfPushNotificationPermissionDenied,
    checkIfSuppressedForever: Guards.checkIfSuppressedForever,
    checkIfSuppressionActive: Guards.checkIfSuppressionActive,
    checkIfLocationPermissionGranted: Guards.checkIfLocationPermissionGranted,
    checkIfLocationPermissionDenied: Guards.checkIfLocationPermissionDenied,
    checkIfLocationGrantTypeAllowOnce: Guards.checkIfLocationGrantTypeAllowOnce,
    checkIfBackgroundLocationPermissionGranted: Guards.checkIfBackgroundLocationPermissionGranted,
    checkIfForegroundLocationPermissionGranted: Guards.checkIfForegroundLocationPermissionGranted,
    checkIfBackgroundLocationPrimerSuppressedForever:
      Guards.checkIfBackgroundLocationPrimerSuppressedForever, // prettier-ignore
    checkIfBackgroundLocationPrimerSuppressionActive:
      Guards.checkIfBackgroundLocationPrimerSuppressionActive, // prettier-ignore
  },
});

// ─────────────────────────────────────────────────────────────────────────────

export const permissionsMachine = model.createMachine({
  id: 'permissions',
  type: 'parallel',
  states: {
    'push-notification-permission': {
      initial: 'waiting',
      states: {
        waiting: {
          always: 'checking-if-enabled',
        },
        'checking-if-enabled': {
          always: [
            { guard: 'checkIfPushNotificationPrimerEnabled', target: 'setup' },
            { target: 'disabled' },
          ],
        },
        setup: {
          invoke: {
            src: 'getPushNotificationPermissionStatus',
            onDone: [
              {
                guard: {
                  type: 'checkIfPushNotificationPermissionGranted',
                  params: (args) => args.event.output,
                },
                target: 'approved',
              },
              {
                guard: {
                  type: 'checkIfPushNotificationPermissionDenied',
                  params: (args) => args.event.output,
                },
                target: 'denied',
              },
              { target: 'checking-if-suppressed' },
            ],
            onError: { target: 'checking-if-suppressed' },
          },
        },
        'checking-if-suppressed': {
          always: [
            { guard: 'checkIfPushNotificationSuppressedForever', target: 'already-shown' },
            { guard: 'checkIfPushNotificationSuppressionActive', target: 'already-shown' },
            { target: 'showing-primer' },
          ],
        },
        'showing-primer': {
          entry: ['onPushNotificationPrimerShow'],
          on: {
            REQUEST_PUSH_NOTIFICATION_PERMISSIONS: {
              target: 'requesting-permission',
              actions: ['onRequestPushNotificationPermission'],
            },
            SKIP_PUSH_NOTIFICATION_PERMISSIONS: {
              target: 'skipping',
              actions: ['onSkipPushNotificationPermission'],
            },
          },
        },
        'requesting-permission': {
          invoke: {
            src: 'requestPushNotificationPermission',
            onDone: [
              {
                guard: {
                  type: 'checkIfPushNotificationPermissionGranted',
                  params: (args) => args.event.output,
                },
                target: 'approved',
                actions: {
                  type: 'onPushNotificationPermissionGranted',
                  params: passPushNotificationPermissionOutput,
                },
              },
              {
                target: 'denied',
                actions: {
                  type: 'onPushNotificationPermissionDenied',
                  params: passPushNotificationPermissionOutput,
                },
              },
            ],
            onError: {
              target: 'denied',
              actions: 'onPushNotificationPermissionDenied',
            },
          },
        },
        skipping: {
          entry: ['persistPushNotificationSuppressedUntil'],
          always: 'skipped',
        },
        approved: { type: 'final' },
        denied: { type: 'final' },
        'already-shown': { type: 'final' },
        skipped: { type: 'final' },
        disabled: { type: 'final' },
      },
    },
    'location-permission': {
      initial: 'waiting',
      states: {
        waiting: {
          always: 'checking-if-enabled',
        },
        'checking-if-enabled': {
          always: [
            { guard: 'checkIfLocationPrimerEnabled', target: 'setup' },
            { target: 'disabled' },
          ],
        },
        setup: {
          invoke: {
            src: 'getLocationPermissionStatus',
            onDone: [
              {
                guard: {
                  type: 'checkIfLocationPermissionGranted',
                  params: (args) => args.event.output,
                },
                target: 'approved',
              },
              {
                guard: {
                  type: 'checkIfLocationPermissionDenied',
                  params: (args) => args.event.output,
                },
                target: 'denied',
              },
              { target: 'checking-if-suppressed' },
            ],
            onError: { target: 'checking-if-suppressed' },
          },
        },
        'checking-if-suppressed': {
          always: [
            { guard: 'checkIfSuppressedForever', target: 'already-shown' },
            { guard: 'checkIfSuppressionActive', target: 'already-shown' },
            {
              guard: 'checkIfLocationGrantTypeAllowOnce',
              target: 'showing-primer',
              actions: ['onAllowOnceDetected'],
            },
            { target: 'showing-primer' },
          ],
        },
        'showing-primer': {
          entry: ['onLocationPrimerShow'],
          on: {
            REQUEST_LOCATION_PERMISSIONS: {
              target: 'requesting-permission',
              actions: ['onRequestLocationPermission'],
            },
            SKIP_LOCATION_PERMISSIONS: {
              target: 'skipping',
              actions: ['onSkipLocationPermission'],
            },
          },
        },
        'requesting-permission': {
          invoke: {
            src: 'requestLocationPermission',
            onDone: [
              {
                guard: {
                  type: 'checkIfLocationPermissionGranted',
                  params: (args) => args.event.output,
                },
                target: 'approved',
                actions: [
                  {
                    type: 'persistLocationGrantType',
                    params: passLocationPermissionOutput,
                  },
                  {
                    type: 'onLocationPermissionGranted',
                    params: passLocationPermissionOutput,
                  },
                ],
              },
              {
                target: 'denied',
                actions: [
                  'clearLocationGrantType',
                  {
                    type: 'onLocationPermissionDenied',
                    params: passLocationPermissionOutput,
                  },
                ],
              },
            ],
            onError: {
              target: 'denied',
              actions: 'onLocationPermissionDenied',
            },
          },
        },
        skipping: {
          entry: ['persistSuppressedUntil'],
          always: 'skipped',
        },
        approved: { type: 'final' },
        denied: { type: 'final' },
        'already-shown': { type: 'final' },
        skipped: { type: 'final' },
        disabled: { type: 'final' },
      },
    },
    'background-location-permission': {
      initial: 'waiting',
      states: {
        waiting: {
          always: 'checking-if-enabled',
        },
        'checking-if-enabled': {
          always: [
            { guard: 'checkIfBackgroundLocationPrimerEnabled', target: 'setup' },
            { target: 'disabled' },
          ],
        },
        setup: {
          invoke: {
            src: 'getBackgroundLocationPermissionStatus',
            onDone: [
              {
                guard: {
                  type: 'checkIfBackgroundLocationPermissionGranted',
                  params: (args) => args.event.output,
                },
                target: 'approved',
              },
              {
                guard: {
                  type: 'checkIfForegroundLocationPermissionGranted',
                  params: (args) => args.event.output,
                },
                target: 'checking-if-suppressed',
              },
              { target: 'ineligible' },
            ],
            onError: { target: 'failed' },
          },
        },
        'checking-if-suppressed': {
          always: [
            {
              guard: 'checkIfBackgroundLocationPrimerSuppressedForever',
              target: 'already-shown',
            },
            {
              guard: 'checkIfBackgroundLocationPrimerSuppressionActive',
              target: 'already-shown',
            },
            { target: 'showing-primer' },
          ],
        },
        'showing-primer': {
          entry: ['onBackgroundLocationPrimerShow'],
          on: {
            REQUEST_BACKGROUND_LOCATION_PERMISSIONS: {
              target: 'requesting-permission',
            },
            SKIP_BACKGROUND_LOCATION_PERMISSIONS: {
              target: 'skipping',
              actions: ['onSkipBackgroundLocationPermission'],
            },
          },
        },
        'requesting-permission': {
          entry: ['onBackgroundLocationUpgradeDialogShow'],
          invoke: {
            src: 'requestBackgroundLocationPermission',
            onDone: [
              {
                guard: {
                  type: 'checkIfLocationPermissionGranted',
                  params: (args) => args.event.output,
                },
                target: 'approved',
                actions: ['onBackgroundLocationPermissionGranted'],
              },
              {
                target: 'denied',
                actions: [
                  'persistBackgroundLocationPrimerSuppressedForever',
                  'onBackgroundLocationPermissionDenied',
                ],
              },
            ],
            onError: { target: 'failed' },
          },
        },
        skipping: {
          entry: ['persistBackgroundLocationPrimerSuppressedUntil'],
          always: 'skipped',
        },
        approved: { type: 'final' },
        denied: { type: 'final' },
        'already-shown': { type: 'final' },
        ineligible: { type: 'final' },
        skipped: { type: 'final' },
        failed: { type: 'final' },
        disabled: { type: 'final' },
      },
    },
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function passPushNotificationPermissionOutput(args: {
  event: { output: RequestPushNotificationPermissionOutput };
}) {
  return args.event.output;
}

function passLocationPermissionOutput(args: {
  event: { output: RequestLocationPermissionOutput };
}) {
  return args.event.output;
}

// ─── Types ───────────────────────────────────────────────────────────────────

export type PermissionsMachineActorRef = ActorRefFrom<typeof permissionsMachine>;
export type PermissionsMachineImplementations = MachineImplementationsFrom<typeof permissionsMachine>; // prettier-ignore
